/**
 * Remove empty string values from an object and construct a URL query string.
 *
 * @param {Record<string, any>} values - The input object containing key-value pairs.
 * @returns {Object} An object with two properties:
 *   - `obj`: The updated object with non-empty properties.
 *   - `urlStr`: The URL query string containing key-value pairs.
 * @example
 * const { obj, urlStr } = removeEmptyStr(values);
 */

export const removeEmptyStr = (values: any) => {
  const queryParams = [];
  let obj = {};

  for (let key in values) {
    const value =
      typeof values[key] === "object" ? values[key]?.query : values[key];

    // Check if the value is not an empty string or undefined
    if (value !== "" && value !== undefined) {
      // Check if the first character of the value is a letter
      if (/[a-zA-Z0-9]/.test(value)) {
        // @ts-ignore
        // If it starts with a letter, treat it as a valid string
        obj[key] = value;
      } else {
        // Otherwise, try to parse it as a number
        const parsedValue = parseInt(value, 10);
        // Check if it's a valid number
        if (!isNaN(parsedValue)) {
          // @ts-ignore
          obj[key] = parsedValue;
        }
      }
    }

    // Encode the key and value for the query string
    const encodedKey = encodeURIComponent(key);
    const encodedValue = encodeURIComponent(
      // @ts-ignore
      typeof obj[key] === "object" ? obj[key]?.query : obj[key]
    );
    //Only push the string if value is not undefined
    value && queryParams.push(`${encodedKey}=${encodedValue}`);
  }

  const urlStr = queryParams.join("&");

  return { obj, urlStr };
};

export function removeKeyFromObj({
  objKey,
  params,
}: {
  params: Record<any, string>;
  objKey: string;
}) {
  let newObj = {};
  for (let key in params) {
    if (key !== objKey) {
      // @ts-ignore
      newObj[key] = params[key];
    }
  }

  return newObj;
}

interface IProps {
  /** @param key the name of the localstorage key you want to save */
  key: string;
  /** @param oldValue the old value gotten from localstorage using `GetItem func` */
  oldValue?: any;
  /** @param newValue the new value you want to save to localstorage */
  newValue?: any;
  /** @param newObjname the name of the new obj you want to add to your existing localstorage object `e.g  props.oldValue[props.newObjName] = props.newValue ` */
  newObjName: string;
  /** @param value the values you want to save to localStorage Note: use this for saving new localstorage items. */
  value?: any;
}

export const LocalStorageFn = {
  getObjectItem: <T extends Record<string, any>>(key: string): T => {
    if (typeof window === "undefined") {
      // We're not running in the browser, so return an empty object
      return {} as T;
    }
    const storedData = localStorage.getItem(key);
    if (storedData) {
      return JSON.parse(storedData) as T;
    }
    return {} as T;
  },
  setObjectItem: <T extends Record<string, any>>(key: string, response: T) => {
    if (typeof window === "undefined") {
      // We're not running in the browser, so do nothing
      return;
    }
    localStorage.setItem(key, JSON.stringify(response));
  },
  setItem: (key: string, value: string) => {
    if (typeof window === "undefined") {
      // We're not running in the browser, so do nothing
      return;
    }
    localStorage.setItem(key, value);
  },
  removeItem: (key: string) => {
    if (typeof window === "undefined") {
      // We're not running in the browser, so do nothing
      return;
    }
    localStorage.removeItem(key);
  },
  getItem: (key: string) => {
    if (typeof window === "undefined") {
      // We're not running in the browser, so do nothing
      return;
    }
    localStorage.getItem(key);
  },
  clearStorage: () => {
    if (typeof window === "undefined") {
      // We're not running in the browser, so do nothing
      return;
    }
    localStorage.clear();
  },

  //true flag to save array of objects in the key.
  setItemOrReplace: (props: IProps & { type?: boolean }) => {
    if (typeof window === "undefined") {
      // We're not running in the browser, so do nothing
      return;
    }
    const keyValue = localStorage.getItem(props.key);
    const oldValue = keyValue && JSON.parse(keyValue || "");

    //this logic to handle object keys that want to accept  arrays of object
    if (props.type) {
      if (oldValue && oldValue[props.newObjName]) {
        oldValue[props.newObjName].push(props.newValue);
        return localStorage.setItem(props.key, JSON.stringify(oldValue));
      } else {
        oldValue[props.newObjName] = props.newValue;
        return localStorage.setItem(props.key, JSON.stringify(oldValue));
      }
    }

    //This Logic to handle objects
    if (oldValue) {
      oldValue[props.newObjName] = props.newValue;
      return localStorage.setItem(props.key, JSON.stringify(oldValue));
    } else {
      const { newObjName } = props;
      return localStorage.setItem(
        props.key,
        JSON.stringify({ [newObjName]: props.newValue })
      );
    }
  },
};

/**
 * Function to generate year
 * {startYear} prop to indicate when the year should start from
 * {numOfYaer} prop to indicate the numbe of years you want substract from the current year e.g 2000 + 10 = 2010 i.e 10 years
 */
export function generateYear({ numofYear }: { numofYear: number }) {
  const date = new Date();
  let currentYear = date.getFullYear();
  const arr = [];
  for (let i = 1; i <= numofYear; i++) {
    arr[i] = { name: currentYear };
    currentYear--;
  }
  return { data: arr };
}

export function parseQueryString() {
  const queryString = window.location.search;
  const params = new URLSearchParams(queryString);
  const queryObject = {};

  params.forEach((value, key) => {
    // @ts-ignore
    queryObject[key] = value;
  });

  return queryObject;
}

export function formatCurrencyNaira(props: number) {
  if (typeof props !== "number") {
    return "Invalid input";
  }

  // Use Intl.NumberFormat to format the number with a dollar sign
  const formattedMoney = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 2,
  }).format(props);

  return formattedMoney;
}
export function formatCurrency(props: number) {
  if (typeof props !== "number") {
    return "Invalid input";
  }

  // Use Intl.NumberFormat to format the number with a dollar sign
  const formattedMoney = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(props);

  return formattedMoney;
}

export function formatNumber(number: number) {
  let decimalPlaces = 0,
    thousandsSeparator = ",",
    decimalSeparator = ".";
  // Ensure the input is a number
  if (typeof number !== "number") {
    console.error("Input is not a number");

    return;
  }

  // Round the number to the specified decimal places
  const roundedNumber = number.toFixed(decimalPlaces);

  // Split the number into integer and decimal parts
  const [integerPart, decimalPart] = roundedNumber.split(".");

  // Add thousands separators to the integer part
  const integerWithSeparators = addThousandsSeparators(
    integerPart,
    thousandsSeparator
  );

  // Combine the integer and decimal parts with the decimal separator
  const formattedNumber = decimalPart
    ? `${integerWithSeparators}${decimalSeparator}${decimalPart}`
    : integerWithSeparators;

  return formattedNumber;
}

function addThousandsSeparators(integerPart: any, separator: any) {
  const parts = [];
  while (integerPart.length > 0) {
    parts.unshift(integerPart.substr(-3));
    integerPart = integerPart.substr(0, integerPart.length - 3);
  }
  return parts.join(separator);
}

// Custom validation function to check if the year is in the future
export const isFutureYear = (value: string) => {
  const currentYear = new Date().getFullYear();
  const enteredYear = parseInt(value, 10);

  return enteredYear >= currentYear;
};

export function convertFormattedNumberToNumber(formattedNumber: string) {
  const numberWithoutCommas = formattedNumber.replace(/,/g, "");
  return parseFloat(numberWithoutCommas);
}
